import { Component, OnInit, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngrx/store';

// Angular
import { Router } from '@angular/router';

// PrimeNG
import { StepperModule } from 'primeng/stepper';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from 'primeng/progressbar';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TreeSelectModule } from 'primeng/treeselect';
import { Tree, TreeModule } from 'primeng/tree';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { MessageModule } from 'primeng/message';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { CheckboxModule } from 'primeng/checkbox';

// Kanzi modules
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { BreadcrumbService } from '@kanzi-apes/kanzi-ui';

// Models
import {
  BatchCreationConfig,
  LocationDepth,
  ConsecutiveFormat,
  BatchDimensions,
  NomenclaturePrefixes,
  DEFAULT_PREFIXES,
} from '../../models/batch-creation-config.model';
import { GeneratedLocationsResult } from '../../models/generated-location.model';
import {
  validatePrefix,
  validatePrefixes,
  generateNomenclatureExample,
  estimateMaxNomenclatureLength,
} from '../../utils/prefix-validator.utils';
import { 
  calculateLocations, 
  formatDuration, 
  isLargeBatch, 
  isVeryLargeBatch,
  getBatchSizeWarning 
} from '../../utils/batch-calculation.utils';
import { BatchLocationGeneratorService } from '../../services/batch-location-generator.service';
import { UbicacionesQueriesService, LocationTreeNode } from '../../queries/ubicaciones-queries.service';
import { WarehousesQueriesService } from '../../queries/warehouses-queries.service';
import { StoresQueriesService } from '../../queries/stores-queries.service';
import { UbicacionBatchService } from '../../services/ubicacion-batch.service';
import { BatchCreationRequestDto } from '../../models/batch-creation-request.model';
import { BatchCreationResponseDto } from '../../models/batch-creation-response.model';

// Store
import * as fromCustomer from '@kanzi-apes/kanzi-customer';

/**
 * Componente para creación masiva de ubicaciones en estructura matricial
 * 
 * Implementa un wizard de 5 pasos:
 * 1. Selección de salón padre
 * 2. Configuración de profundidad
 * 3. Dimensiones y prefijos personalizables
 * 4. Preview de ubicaciones
 * 5. Confirmación y ejecución
 * 
 * @example
 * // Uso en routing
 * {
 *   path: 'batch-create',
 *   component: UbicacionBatchCreateComponent
 * }
 */
@Component({
    selector: 'kanzi-apes-ubicacion-batch-create',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        StepperModule,
        ButtonModule,
        DropdownModule,
        TreeSelectModule,
        TreeModule,
        InputNumberModule,
        InputTextModule,
        RadioButtonModule,
        SelectButtonModule,
        ToggleButtonModule,
        CardModule,
        DividerModule,
        MessageModule,
        TooltipModule,
        TagModule,
        CheckboxModule,
        ProgressSpinnerModule,
        ProgressBarModule,
        AccordionModule,
        KanziUiModule,
    ],
    providers: [MessageService],
    templateUrl: './ubicacion-batch-create.component.html',
    styleUrls: ['./ubicacion-batch-create.component.scss']
})
export class UbicacionBatchCreateComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly store = inject(Store);
  private readonly generatorService = inject(BatchLocationGeneratorService);
  private readonly queries = inject(UbicacionesQueriesService);
  private readonly warehouseQueries = inject(WarehousesQueriesService);
  private readonly storeQueries = inject(StoresQueriesService);
  private readonly batchService = inject(UbicacionBatchService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);

  // ViewChild para acceder al componente Tree
  @ViewChild('treeComponent') treeComponent?: Tree;

  // Export type for template
  readonly LocationDepth = {} as LocationDepth;

  // ========== Signals ==========

  /**
   * Paso actual del wizard (0-4)
   */
  activeStep = signal<number>(0);

  /**
   * Tipo de ubicación origen: 'warehouse' o 'store'
   */
  locationType = signal<'warehouse' | 'store'>('warehouse');

  /**
   * Opciones para el selector de tipo de ubicación
   */
  locationTypeOptions = [
    { label: 'batch_create.step1.warehouse_label', value: 'warehouse', icon: 'pi pi-warehouse' },
    { label: 'batch_create.step1.store_label', value: 'store', icon: 'pi pi-shop' },
  ];

  /**
   * Configuración del batch actual
   */
  config = signal<Partial<BatchCreationConfig>>({
    prefixes: { ...DEFAULT_PREFIXES },
    consecutiveFormat: 'numeric' as ConsecutiveFormat,
    dimensions: {
      positions: 10,
    },
  });

  /**
   * Bodega (warehouse) seleccionada para filtrar salones
   */
  selectedWarehouse = signal<any>(null);

  /**
   * Tienda (store) seleccionada para filtrar ubicaciones hijas
   */
  selectedStore = signal<any>(null);

  /**
   * Filtros para obtener la lista de bodegas
   */
  warehouseFilters = signal<any>(null);

  /**
   * Filtros para obtener la lista de tiendas
   */
  storeFilters = signal<any>(null);

  /**
   * Filtros para obtener ubicaciones tipo SALON filtradas por bodega
   */
  salonFilters = signal<{ type__name?: string; parent_id?: number }>({ type__name: 'SALON' });

  /**
   * Filtros para obtener ubicaciones hijas de una tienda (sin filtro de tipo)
   */
  storeChildFilters = signal<{ parent_id?: number }>({});

  /**
   * Ubicación padre seleccionada (salón para warehouse, ubicación hija para store)
   */
  selectedSalon = signal<any>(null);

  /**
   * Profundidad seleccionada (1-4)
   */
  depth = signal<LocationDepth | null>(null);

  /**
   * Dimensiones configuradas
   */
  dimensions = signal<Partial<BatchDimensions>>({
    positions: 10,
  });

  /**
   * Prefijos personalizados
   */
  prefixes = signal<NomenclaturePrefixes>({ ...DEFAULT_PREFIXES });

  /**
   * Formato de consecutivo
   */
  consecutiveFormat = signal<ConsecutiveFormat>('numeric');

  /**
   * Dígitos de padding (cuando consecutiveFormat = 'padded')
   */
  paddingDigits = signal<number>(2);

  /**
   * Ubicaciones generadas (resultado del preview)
   */
  generatedResult = signal<GeneratedLocationsResult | null>(null);

  /**
   * Checkbox de confirmación final
   */
  confirmCreation = signal<boolean>(false);

  /**
   * Loading states
   */
  isGenerating = signal<boolean>(false);
  isCreating = signal<boolean>(false);

  /**
   * Resultado de la creación exitosa
   */
  creationResult = signal<BatchCreationResponseDto | null>(null);

  /**
   * Indica si la creación fue exitosa
   */
  creationSuccess = signal<boolean>(false);

  /**
   * Error de creación (si aplica)
   */
  creationError = signal<any>(null);

  /**
   * Mensaje de error amigable
   */
  errorMessage = signal<string>('');

  /**
   * Detalles adicionales del error
   */
  errorDetails = signal<string[]>([]);

  /**
   * Mensaje de estado actual durante creación
   */
  statusMessage = signal<string>('');

  /**
   * Errores de validación
   */
  validationErrors = signal<string[]>([]);

  // ========== Queries ==========

  /**
   * Query para obtener lista de bodegas (warehouses)
   */
  warehousesQuery = this.warehouseQueries.useWarehousesList(this.warehouseFilters);

  /**
   * Query para obtener lista de tiendas (stores)
   */
  storesQuery = this.storeQueries.useStoresList(this.storeFilters);

  /**
   * Indica si la query de salones está habilitada (solo cuando hay bodega seleccionada en modo warehouse)
   */
  isSalonQueryEnabled = computed(() => this.locationType() === 'warehouse' && !!this.selectedWarehouse());

  /**
   * Indica si la query de ubicaciones hijas de tienda está habilitada
   */
  isStoreChildQueryEnabled = computed(() => this.locationType() === 'store' && !!this.selectedStore());

  /**
   * Query para obtener árbol de ubicaciones tipo SALON filtrado por bodega
   */
  salonTreeQuery = this.queries.useUbicacionesTree(this.salonFilters, this.isSalonQueryEnabled);

  /**
   * Query para obtener árbol de ubicaciones hijas de una tienda (sin exclusión de tipo)
   */
  storeChildTreeQuery = this.queries.useChildLocationsTree(this.storeChildFilters, this.isStoreChildQueryEnabled);

  // ========== Computed Values ==========

  /**
   * Customer seleccionado desde el store
   */
  customerSelected = computed(() => {
    let customer: any = null;
    this.store.select(fromCustomer.getCustomerSelected).subscribe(c => customer = c);
    return customer;
  });

  /**
   * Lista de bodegas formateada para el dropdown
   */
  warehouseOptions = computed(() => {
    const data = this.warehousesQuery.data();
    if (!data?.results) return [];
    return data.results.map(w => ({
      label: `${w.name}${w.display_name ? ' - ' + w.display_name : ''}`,
      value: w,
    }));
  });

  /**
   * Lista de tiendas formateada para el dropdown
   */
  storeOptions = computed(() => {
    const data = this.storesQuery.data();
    if (!data?.results) return [];
    return data.results.map(s => ({
      label: `${s.name}${s.display_name ? ' - ' + s.display_name : ''}`,
      value: s,
    }));
  });

  /**
   * Estado de carga de las bodegas
   */
  isWarehousesLoading = computed(() => this.warehousesQuery.isPending());

  /**
   * Estado de carga de las tiendas
   */
  isStoresLoading = computed(() => this.storesQuery.isPending());

  /**
   * Árbol de salones para TreeSelect (modo warehouse)
   */
  salonTree = computed(() => {
    const tree = this.salonTreeQuery.data();
    if (!tree) return [];
    return this.convertToTreeSelectNodes(tree);
  });

  /**
   * Árbol de ubicaciones hijas para TreeSelect (modo store)
   */
  storeChildTree = computed(() => {
    const tree = this.storeChildTreeQuery.data();
    if (!tree) return [];
    return this.convertToTreeSelectNodes(tree);
  });

  /**
   * Información del salón seleccionado
   */
  selectedSalonInfo = computed(() => {
    const selected = this.selectedSalon();
    if (!selected?.data) return null;
    
    return {
      id: selected.data.id,
      name: selected.data.name,
      displayName: selected.data.display_name,
      externalId: selected.data.external_id,
      type: selected.data.type,
    };
  });

  /**
   * Estado de carga del TreeSelect
   */
  isSalonTreeLoading = computed(() => this.salonTreeQuery.isPending());

  /**
   * Estado de carga del TreeSelect de hijos de tienda
   */
  isStoreChildTreeLoading = computed(() => this.storeChildTreeQuery.isPending());

  /**
   * Indica si el paso 1 es válido según el tipo de ubicación
   */
  isStep1Valid = computed(() => {
    if (this.locationType() === 'warehouse') {
      return !!this.selectedWarehouse() && !!this.selectedSalon();
    } else {
      return !!this.selectedStore() && !!this.selectedSalon();
    }
  });

  /**
   * Indica si el paso 2 (profundidad) es válido
   */
  isStep2Valid = computed(() => {
    return !!this.depth();
  });

  /**
   * Indica si el paso 3 (dimensiones y prefijos) es válido
   */
  isStep3Valid = computed(() => {
    const depth = this.depth();
    const dims = this.dimensions();
    const prefixes = this.prefixes();

    if (!depth) return false;

    // Validar dimensiones según profundidad
    if (depth >= 2 && !dims.rows) return false;
    if (depth >= 3 && !dims.racks) return false;
    if (depth === 4 && !dims.levels) return false;
    if (!dims.positions) return false;

    // Validar prefijos
    const prefixesValidation = validatePrefixes(prefixes);
    if (!prefixesValidation.isValid) return false;

    return true;
  });

  /**
   * Total de ubicaciones a crear
   */
  totalLocations = computed(() => {
    const depth = this.depth();
    const dims = this.dimensions();
    
    if (!depth) return 0;

    const result = calculateLocations({
      depth,
      dimensions: dims as BatchDimensions,
    } as BatchCreationConfig);

    return result.total;
  });

  /**
   * Desglose de ubicaciones por tipo
   */
  locationsByType = computed(() => {
    const depth = this.depth();
    const dims = this.dimensions();
    
    if (!depth) return null;

    const result = calculateLocations({
      depth,
      dimensions: dims as BatchDimensions,
    } as BatchCreationConfig);

    return result.byType;
  });

  /**
   * Tiempo estimado de creación
   */
  estimatedTime = computed(() => {
    const total = this.totalLocations();
    const result = calculateLocations({
      depth: this.depth() || 1,
      dimensions: this.dimensions() as BatchDimensions,
    } as BatchCreationConfig);

    return formatDuration(result.estimatedSeconds);
  });

  /**
   * Indica si es un batch grande (>= 500 ubicaciones)
   */
  isLargeBatchComputed = computed(() => {
    return isLargeBatch(this.totalLocations());
  });

  /**
   * Indica si es un batch muy grande (>= 1000 ubicaciones)
   */
  isVeryLargeBatchComputed = computed(() => {
    return isVeryLargeBatch(this.totalLocations());
  });

  /**
   * Mensaje de advertencia según tamaño del batch
   */
  batchSizeWarning = computed(() => {
    return getBatchSizeWarning(this.totalLocations());
  });

  /**
   * Ejemplo de nomenclatura según configuración actual
   */
  nomenclatureExample = computed(() => {
    const depth = this.depth();
    const prefixes = this.prefixes();

    if (!depth) return '';

    // Asegurar que todos los prefixes están definidos
    const completePrefixes: Required<NomenclaturePrefixes> = {
      row: prefixes.row || DEFAULT_PREFIXES.row,
      rack: prefixes.rack || DEFAULT_PREFIXES.rack,
      level: prefixes.level || DEFAULT_PREFIXES.level,
      position: prefixes.position || DEFAULT_PREFIXES.position,
    };

    return generateNomenclatureExample(completePrefixes, depth);
  });

  /**
   * Longitud máxima estimada de nomenclatura
   */
  maxNomenclatureLength = computed(() => {
    const depth = this.depth();
    const prefixes = this.prefixes();
    const dims = this.dimensions();
    const parentName = this.selectedSalon()?.name || 'SALON';

    if (!depth) return 0;

    // Obtener el consecutivo máximo según profundidad
    const maxConsecutive = depth === 1 
      ? (dims.positions || 10)
      : depth === 2
        ? Math.max(dims.rows || 5, dims.positions || 10)
        : depth === 3
          ? Math.max(dims.rows || 5, dims.racks || 4, dims.positions || 10)
          : Math.max(dims.rows || 5, dims.racks || 4, dims.levels || 3, dims.positions || 10);

    // Asegurar que todos los prefixes están definidos
    const completePrefixes: Required<NomenclaturePrefixes> = {
      row: prefixes.row || DEFAULT_PREFIXES.row,
      rack: prefixes.rack || DEFAULT_PREFIXES.rack,
      level: prefixes.level || DEFAULT_PREFIXES.level,
      position: prefixes.position || DEFAULT_PREFIXES.position,
    };

    return estimateMaxNomenclatureLength(
      parentName,
      completePrefixes,
      depth,
      maxConsecutive
    );
  });

  /**
   * Indica si se puede avanzar al siguiente paso
   */
  canGoNext = computed(() => {
    const step = this.activeStep();
    
    switch (step) {
      case 0:
        return this.isStep1Valid();
      case 1:
        return this.isStep2Valid();
      case 2:
        return this.isStep3Valid();
      case 3:
        return !!this.generatedResult();
      default:
        return false;
    }
  });

  /**
   * Indica si se puede retroceder al paso anterior
   */
  canGoBack = computed(() => {
    return this.activeStep() > 0;
  });

  /**
   * Errores de validación de prefijos
   */
  prefixValidationErrors = computed(() => {
    const prefixes = this.prefixes();
    const validation = validatePrefixes(prefixes);
    
    if (validation.isValid) return [];
    
    return validation.allErrors || [];
  });

  /**
   * Mensaje de advertencia si hay errores de prefijos
   */
  prefixErrorMessage = computed(() => {
    const errors = this.prefixValidationErrors();
    if (errors.length === 0) return null;
    
    return errors.join('. ');
  });

  /**
   * Árbol de ubicaciones para PrimeNG Tree en preview
   */
  previewTree = computed(() => {
    const result = this.generatedResult();
    if (!result || !result.locations) return [];
    
    return this.convertGeneratedLocationsToTree(result.locations);
  });

  /**
   * Estadísticas del preview
   */
  previewStats = computed(() => {
    const result = this.generatedResult();
    if (!result) return null;

    return {
      total: result.statistics.total,
      byType: result.statistics.byType,
      firstLocation: result.locations[0]?.name || '',
      lastLocation: result.locations[result.locations.length - 1]?.name || '',
      preview: result.preview,
    };
  });

  /**
   * Indica si el paso 4 (preview) es válido
   */
  isStep4Valid = computed(() => {
    return !!this.generatedResult() && this.generatedResult()!.locations.length > 0;
  });

  /**
   * Indica si se puede crear (paso 5 con confirmación)
   */
  canCreate = computed(() => {
    return this.isStep4Valid() && this.confirmCreation();
  });

  // ========== Lifecycle Hooks ==========

  ngOnInit(): void {
    this.breadcrumbService.setItems([
      {
        label: 'Ubicaciones',
        routerLink: '/locations',
      },
      {
        label: 'Creación en Batch',
        icon: 'pi pi-plus-circle',
      },
    ]);
  }

  // ========== Navigation Methods ==========

  /**
   * Avanza al siguiente paso del wizard
   */
  nextStep(): void {
    if (!this.canGoNext()) return;

    const currentStep = this.activeStep();

    // Si estamos en el paso 2 (configuración), generar preview
    if (currentStep === 2) {
      this.generatePreview();
    }

    this.activeStep.set(currentStep + 1);
  }

  /**
   * Retrocede al paso anterior del wizard
   */
  previousStep(): void {
    if (!this.canGoBack()) return;

    const currentStep = this.activeStep();
    this.activeStep.set(currentStep - 1);
  }

  /**
   * Navega a un paso específico
   */
  goToStep(step: number): void {
    if (step < 0 || step > 4) return;
    this.activeStep.set(step);
  }

  // ========== Configuration Methods ==========

  /**
   * Maneja el cambio de tipo de ubicación (warehouse/store)
   * Resetea las selecciones al cambiar de tipo
   */
  onLocationTypeChange(type: 'warehouse' | 'store'): void {
    this.locationType.set(type);
    this.selectedWarehouse.set(null);
    this.selectedStore.set(null);
    this.selectedSalon.set(null);
    this.salonFilters.set({ type__name: 'SALON' });
    this.storeChildFilters.set({});
    this.updateConfig();
  }

  /**
   * Maneja la selección de la bodega
   * Al cambiar la bodega, resetea el salón y actualiza los filtros del tree de salones
   */
  onWarehouseSelected(warehouse: any): void {
    this.selectedWarehouse.set(warehouse);
    this.selectedSalon.set(null);

    if (warehouse?.id) {
      this.salonFilters.set({ type__name: 'SALON', parent_id: warehouse.id });
    } else {
      this.salonFilters.set({ type__name: 'SALON' });
    }
    this.updateConfig();
  }

  /**
   * Maneja la selección de la tienda
   * Al cambiar la tienda, resetea la ubicación hija y actualiza los filtros
   */
  onStoreSelected(store: any): void {
    this.selectedStore.set(store);
    this.selectedSalon.set(null);

    if (store?.id) {
      this.storeChildFilters.set({ parent_id: store.id });
    } else {
      this.storeChildFilters.set({});
    }
    this.updateConfig();
  }

  /**
   * Maneja la selección del salón padre o ubicación hija de tienda
   */
  onSalonSelected(salon: any): void {
    // Cuando se selecciona desde TreeSelect, event puede ser el TreeNode o event.node
    const node = salon?.node || salon;
    this.selectedSalon.set(node);
    this.updateConfig();
  }

  /**
   * Convierte LocationTreeNode[] a TreeNode[] para PrimeNG TreeSelect
   */
  private convertToTreeSelectNodes(locations: LocationTreeNode[]): any[] {
    return locations.map((loc) => this.toTreeSelectNode(loc));
  }

  /**
   * Convierte recursivamente LocationTreeNode a TreeNode de PrimeNG
   */
  private toTreeSelectNode(location: LocationTreeNode): any {
    return {
      key: location.id?.toString() || location.name || 'unknown',
      label: `${location.name}${location.display_name ? ' - ' + location.display_name : ''}`,
      data: location, // Guardamos el objeto completo en data
      type: location.type?.name || 'UNKNOWN',
      icon: this.getIconForType(location.type?.name),
      children:
        location.children?.map((child) => this.toTreeSelectNode(child)) || [],
    };
  }

  /**
   * Obtiene el ícono según el tipo de ubicación
   */
  getIconForType(typeName?: string): string {
    const icons: Record<string, string> = {
      SALON: 'pi pi-building',
      WAREHOUSE: 'pi pi-warehouse',
      ROW: 'pi pi-bars',
      RACK: 'pi pi-th-large',
      LEVEL: 'pi pi-align-justify',
      POSITION: 'pi pi-box',
    };
    return icons[typeName || ''] || 'pi pi-map-marker';
  }

  /**
   * Obtiene el severity para p-tag según el tipo de ubicación
   */
  getTagSeverity(typeName?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' {
    const severities: Record<string, any> = {
      SALON: 'info',
      ROW: 'success',
      RACK: 'warning',
      LEVEL: 'secondary',
      POSITION: 'contrast',
    };
    return severities[typeName || ''] || 'info';
  }

  /**
   * Convierte ubicaciones generadas a TreeNode[] para PrimeNG Tree
   * Construye estructura jerárquica basada en parentPath
   * Los nodos están colapsados por defecto excepto el primer nivel para mejor UX
   */
  private convertGeneratedLocationsToTree(locations: any[]): any[] {
    const map = new Map<string, any>();
    const roots: any[] = [];

    // Primer paso: crear todos los nodos
    locations.forEach((loc, index) => {
      const node = {
        key: loc.name || `loc-${index}`,
        label: loc.name,
        data: loc,
        type: loc.type,
        icon: this.getIconForType(loc.type),
        children: [],
        expanded: false, // Colapsado por defecto para mejor UX con muchos elementos
      };
      map.set(loc.name, node);
    });

    // Segundo paso: construir la jerarquía
    locations.forEach((loc) => {
      const node = map.get(loc.name);
      if (!node) return;

      if (loc.parent_name) {
        // Tiene padre, agregarlo como hijo
        const parentNode = map.get(loc.parent_name);
        if (parentNode) {
          parentNode.children.push(node);
        } else {
          // Si no encuentra el padre, agregar como raíz
          roots.push(node);
        }
      } else {
        // No tiene padre, es raíz
        roots.push(node);
      }
    });

    // Expandir solo el primer nivel (raíces) para mostrar estructura inicial
    roots.forEach(root => {
      root.expanded = true;
    });

    return roots;
  }

  /**
   * Maneja el cambio de profundidad
   */
  onDepthChange(depth: LocationDepth): void {
    this.depth.set(depth);
    
    // Resetear dimensiones según profundidad
    const dims: Partial<BatchDimensions> = {
      positions: this.dimensions().positions || 10,
    };

    if (depth >= 2) {
      dims.rows = this.dimensions().rows || 5;
    }
    if (depth >= 3) {
      dims.racks = this.dimensions().racks || 4;
    }
    if (depth === 4) {
      dims.levels = this.dimensions().levels || 3;
    }

    this.dimensions.set(dims);
    this.updateConfig();
  }

  /**
   * Maneja el cambio en dimensiones
   */
  onDimensionChange(field: keyof BatchDimensions, value: number | null): void {
    const numValue = value || 1;
    this.dimensions.update(dims => ({
      ...dims,
      [field]: numValue,
    }));
    this.updateConfig();
  }

  /**
   * Maneja el cambio en prefijos
   */
  onPrefixChange(level: keyof NomenclaturePrefixes, value: string): void {
    // Validar el prefijo
    const validation = validatePrefix(value, level);
    
    if (validation.isValid) {
      this.prefixes.update(prefixes => ({
        ...prefixes,
        [level]: value,
      }));
      this.updateConfig();
    }
  }

  /**
   * Maneja el cambio de formato de consecutivo
   */
  onConsecutiveFormatChange(format: ConsecutiveFormat): void {
    this.consecutiveFormat.set(format);
    this.updateConfig();
  }

  /**
   * Maneja el cambio de dígitos de padding
   */
  onPaddingDigitsChange(digits: number | null): void {
    this.paddingDigits.set(digits || 2);
    this.updateConfig();
  }

  /**
   * Actualiza la configuración completa
   */
  private updateConfig(): void {
    this.config.update(config => ({
      ...config,
      parentSalonId: this.selectedSalon()?.data?.id,
      parentSalonName: this.selectedSalon()?.data?.name,
      depth: this.depth() || undefined,
      dimensions: this.dimensions() as BatchDimensions,
      prefixes: this.prefixes(),
      consecutiveFormat: this.consecutiveFormat(),
      paddingDigits: this.consecutiveFormat() === 'padded' 
        ? this.paddingDigits() 
        : undefined,
      customerId: (this.customerSelected() as any)?.id,
    }));
  }

  // ========== Generation Methods ==========

  /**
   * Genera el preview de ubicaciones
   */
  generatePreview(): void {
    try {
      this.isGenerating.set(true);
      this.validationErrors.set([]);

      const config = this.config() as BatchCreationConfig;
      const result = this.generatorService.generateLocations(config);

      this.generatedResult.set(result);
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Error al generar ubicaciones';
      
      this.validationErrors.set([errorMessage]);
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Cancels the batch creation wizard and navigates to the ubicaciones list.
   * @returns void
   */
  cancel(): void {
    this.router.navigate(['/locations/ubicaciones/list']);
  }

  /**
   * Resetea el wizard a su estado inicial
   */
  reset(): void {
    this.activeStep.set(0);
    this.locationType.set('warehouse');
    this.selectedWarehouse.set(null);
    this.selectedStore.set(null);
    this.selectedSalon.set(null);
    this.salonFilters.set({ type__name: 'SALON' });
    this.storeChildFilters.set({});
    this.depth.set(null);
    this.dimensions.set({ positions: 10 });
    this.prefixes.set({ ...DEFAULT_PREFIXES });
    this.consecutiveFormat.set('numeric');
    this.paddingDigits.set(2);
    this.generatedResult.set(null);
    this.validationErrors.set([]);
  }

  // ========== Phase 6: Backend Integration Methods ==========

  /**
   * Crea las ubicaciones en batch llamando al backend
   * Maneja el proceso completo incluyendo feedback visual y navegación
   */
  createLocations(): void {
    const result = this.generatedResult();
    const salon = this.selectedSalon();

    if (!result || !salon || !salon.data) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No hay ubicaciones generadas para crear o salón padre inválido',
      });
      return;
    }

    // Construir el DTO desde las signals
    const dimensions = this.dimensions();
    const depth = this.depth()!;
    const customerState = this.store.selectSignal(fromCustomer.getCustomerSelected)();
    
    // Acceder al objeto data dentro del TreeNode
    const salonData = salon.data;
    
    const request: BatchCreationRequestDto = {
      parent_salon_id: salonData.id,
      parent_salon_name: salonData.name,
      depth: depth,
      dimensions: {
        rows: dimensions.rows,
        racks: dimensions.racks,
        levels: dimensions.levels,
        positions: dimensions.positions ?? 10,
      },
      prefixes: this.prefixes(),
      consecutive_format: this.consecutiveFormat(),
      padding_digits: this.paddingDigits(),
      customer_id: customerState?.id ?? 0,
    };

    // Activar estado de loading
    this.isCreating.set(true);
    this.creationSuccess.set(false);
    this.creationError.set(null);
    this.errorMessage.set('');
    this.errorDetails.set([]);
    this.statusMessage.set('Creando ubicaciones...');

    // Llamar al servicio
    this.batchService.createBatch(request).subscribe({
      next: (response: BatchCreationResponseDto) => {
        // Normalizar la respuesta del API para que el template siempre tenga
        // los campos que necesita, independientemente de la versión del backend.
        const totalCreated = response.total_created ?? response.statistics?.total_created ?? 0;
        const normalizedResponse: BatchCreationResponseDto = {
          ...response,
          // Alias de duración: el API devuelve execution_time_seconds
          duration_seconds: response.execution_time_seconds ?? response.duration_seconds ?? 0,
          // IDs de ubicaciones creadas (extraídos de created_locations si no vienen directamente)
          created_location_ids:
            response.created_location_ids ??
            (response.created_locations?.map((l) => l.id) ?? []),
          statistics: {
            ...response.statistics,
            // total_created viene a nivel raíz en el API real
            total_created: totalCreated,
            // success_rate no viene del API, se calcula en el cliente
            success_rate: totalCreated > 0 ? 100 : 0,
          },
        };

        this.isCreating.set(false);
        this.creationSuccess.set(true);
        this.creationResult.set(normalizedResponse);
        this.statusMessage.set('¡Ubicaciones creadas exitosamente!');

        // Toast de éxito
        this.messageService.add({
          severity: 'success',
          summary: 'Creación Exitosa',
          detail: `${totalCreated} ubicaciones creadas correctamente`,
          life: 5000,
        });

        // Avanzar al paso de resultado (paso 5)
        this.activeStep.set(5);
      },
      error: (error: any) => {
        // Error
        this.isCreating.set(false);
        this.creationSuccess.set(false);
        this.creationError.set(error);
        
        // Procesar el error
        const errorInfo = this.handleBatchCreationError(error);
        this.errorMessage.set(errorInfo.message);
        this.errorDetails.set(errorInfo.details);
        this.statusMessage.set('Error al crear ubicaciones');

        // Toast de error
        this.messageService.add({
          severity: 'error',
          summary: 'Error en la Creación',
          detail: errorInfo.message,
          life: 8000,
        });

        // Avanzar al paso de error (paso 6)
        this.activeStep.set(6);
      },
    });
  }

  /**
   * Maneja los errores del batch creation mapeando códigos HTTP a mensajes en español
   * @param error Error de la llamada HTTP
   * @returns Objeto con mensaje y detalles del error
   */
  private handleBatchCreationError(error: any): { message: string; details: string[] } {
    const details: string[] = [];
    let message = 'Error desconocido al crear las ubicaciones';

    // Mapear códigos de error según especificación del backend
    if (error.status) {
      switch (error.status) {
        case 400:
          message = 'Parámetros de entrada inválidos';
          if (error.error?.errors) {
            const errorValues = Object.values(error.error.errors) as string[];
            details.push(...errorValues);
          } else if (error.error?.detail) {
            details.push(error.error.detail);
          }
          break;

        case 403:
          message = 'No tienes permisos para crear ubicaciones';
          details.push('Contacta al administrador del sistema para obtener los permisos necesarios');
          break;

        case 404:
          message = 'La ubicación padre no existe';
          details.push('El salón seleccionado pudo haber sido eliminado');
          details.push('Selecciona otro salón y vuelve a intentar');
          break;

        case 409:
          message = 'Conflicto: Algunas ubicaciones ya existen';
          if (error.error?.existing_codes) {
            details.push(`Códigos duplicados: ${error.error.existing_codes.join(', ')}`);
          }
          if (error.error?.detail) {
            details.push(error.error.detail);
          }
          break;

        case 500:
          message = 'Error interno del servidor';
          details.push('Ocurrió un error inesperado en el servidor');
          details.push('El equipo técnico ha sido notificado');
          details.push('Por favor intenta nuevamente en unos momentos');
          break;

        case 503:
          message = 'Servicio no disponible temporalmente';
          details.push('El servidor está experimentando alta carga');
          details.push('Por favor intenta nuevamente en unos minutos');
          break;

        default:
          message = `Error HTTP ${error.status}`;
          if (error.error?.detail) {
            details.push(error.error.detail);
          }
      }
    } else if (error.message) {
      // Error de red o timeout
      message = 'Error de conexión';
      details.push(error.message);
      details.push('Verifica tu conexión a internet');
    }

    return { message, details };
  }

  // ========== Navigation Methods ==========

  /**
   * Navega al listado de ubicaciones tras una creación masiva exitosa.
   */
  navigateToLocations(): void {
    this.router.navigate(['/locations/ubicaciones/list']);
  }

  /**
   * Navega al detalle del salón padre
   */
  navigateToParentSalon(): void {
    const salon = this.selectedSalon();
    if (salon) {
      this.router.navigate(['/locations', salon.id]);
    }
  }

  /**
   * Resetea el wizard completamente para crear un nuevo batch
   */
  resetWizard(): void {
    // Resetear todas las signals a su estado inicial
    this.reset();
    
    // Resetear signals de Phase 6
    this.isCreating.set(false);
    this.creationResult.set(null);
    this.creationSuccess.set(false);
    this.creationError.set(null);
    this.errorMessage.set('');
    this.errorDetails.set([]);
    this.statusMessage.set('');

    // Volver al paso inicial
    this.activeStep.set(0);

    // Toast informativo
    this.messageService.add({
      severity: 'info',
      summary: 'Wizard Reseteado',
      detail: 'Puedes crear un nuevo lote de ubicaciones',
      life: 3000,
    });
  }

  /**
   * Reintentar la creación con los mismos parámetros
   */
  retryCreation(): void {
    // Volver al paso de confirmación
    this.activeStep.set(4);
    
    // Limpiar errores previos
    this.creationError.set(null);
    this.errorMessage.set('');
    this.errorDetails.set([]);
    this.statusMessage.set('');

    this.messageService.add({
      severity: 'info',
      summary: 'Reintentando',
      detail: 'Revisa la configuración y confirma nuevamente',
      life: 3000,
    });
  }

  // ========== Tree Control Methods ==========

  /**
   * Expande todos los nodos del árbol de preview
   */
  expandAll(): void {
    const tree = this.previewTree();
    this.expandRecursive(tree, true);
    // Forzar actualización del árbol
    this.generatedResult.update(result => ({ ...result! }));
  }

  /**
   * Colapsa todos los nodos del árbol de preview
   */
  collapseAll(): void {
    const tree = this.previewTree();
    this.expandRecursive(tree, false);
    // Forzar actualización del árbol
    this.generatedResult.update(result => ({ ...result! }));
  }

  /**
   * Método auxiliar recursivo para expandir/colapsar nodos
   */
  private expandRecursive(nodes: any[], expand: boolean): void {
    if (!nodes) return;
    
    nodes.forEach(node => {
      node.expanded = expand;
      if (node.children && node.children.length > 0) {
        this.expandRecursive(node.children, expand);
      }
    });
  }
}
